import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { Navigate, useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Title } from "../../../../themes/Default/components/Title";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../themes/Default/components/Form/components/Input";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { Textarea } from "../../../../themes/Default/components/Form/components/Textarea";
import { List } from "../../../../themes/Default/components/List";
import { prepareSelect } from "../../../../services/core/Select";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { ColorPicker } from "../../../../themes/Default/components/Form/components/ColorPicker";
import TimePicker from "../../../../themes/Default/components/Form/components/TimePicker/TimePicker";
import DatePicker from "../../../../themes/Default/components/Form/components/DatePicker/DatePicker";
import { meetingManagement } from "../../../../hooks/api/MeetingManagement";
import { useTranslation } from "react-i18next";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { roles } from "../../../../services/utils/Roles";
import { useUserToken } from "../../../../hooks/core/UserToken";

const AddEvent = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const { useAddMeetingMutation, useParticipantsListMutation } = meetingManagement;
    const [participantsList] = useParticipantsListMutation();
    const [addMeeting, { isLoading: isAddingMeeting }] = useAddMeetingMutation();
    const { useGroupsListMutation, useListGroupParticipantsMutation } = groupsManagement;
    const [listGroupParticipants] = useListGroupParticipantsMutation();
    const [viewGroups] = useGroupsListMutation();
    const [groupsList, setGroupsList] = useState([]);
    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();

    const tabs = [{ title: t("calendar.addEvent"), isActive: true }];

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        addMeeting(values).then((result) => {       
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                navigate("/calendar-featured/day-view");
            }
        });
    }

    const onRemoveSelectedParticipant = (id) => {
        setSelectedParticipants((prev) => prev.filter((participant) => participant != id));
    }

    const getFirstNameById = (id) => {
        const found = participants.find((item) => {
            return item.id == id;
        });
        
        return found ? found.firstName : null;
    }

    const getLastNameById = (id) => {
        const found = participants.find((item) => {
            return item.id == id;
        });
        
        return found ? found.lastName : null;
    }

    useEffect(() => {
        participantsList().then((result) => {
            let options = [];
            result?.data?.records.forEach((participant) => {
                options.push({
                    id: participant.id,
                    name: participant.firstName + " " + participant.lastName + " " + participant.aditionalInfo,
                    firstName: participant.firstName,
                    lastName: participant.lastName
                });
            });
            setParticipants(prepareSelect(options));
        });

        viewGroups().then((result) => {
            let options = [];
            result?.data?.records.map((group) => {
                options.push(
                    {
                        id: group.id,
                        name: group.name + ' - ' + group.languageName
                    }
                );
            })
            setGroupsList(prepareSelect(options));
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <Button title={t("save")} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("calendar.title")} icon="fa-calendar-lines" />
                    <Link title={t("calendar.addEvent")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        title: "",
                        description: "",
                        date: "",
                        color: "",
                        timeTo: "",
                        timeFrom: "",
                        participants: [],
                        participantsSelect: "",
                        groupsSelect: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue, resetForm }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Input
                                        name="title"
                                        label={t("calendar.name")}
                                        required={true}
                                        value={values.title}
                                        onChange={handleChange}
                                        error={formErrors?.title}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        rows={6}
                                        name="description"
                                        label={t("calendar.description")}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={formErrors?.description}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <DatePicker
                                        name="date"
                                        label={t("calendar.date")}
                                        required={true}
                                        value={values.date}
                                        onChange={handleChange}
                                        error={formErrors?.date}
                                        disablePast={true}
                                    />
                                </Field>
                                <Field>
                                    <ColorPicker
                                        name="color"
                                        label={t("noteTypes.color")}
                                        required={true}
                                        value={values.color}
                                        onChange={handleChange}
                                        error={formErrors?.color}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <TimePicker
                                        name="timeFrom"
                                        label={t("calendar.timeFrom")}
                                        required={true}
                                        value={values.timeFrom}
                                        onChange={handleChange}
                                        error={formErrors?.timeFrom}
                                    />
                                </Field>
                                <Field>
                                    <TimePicker
                                        name="timeTo"
                                        label={t("calendar.timeTo")}
                                        required={true}
                                        value={values.timeTo}
                                        onChange={handleChange}
                                        error={formErrors?.timeTo}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR) ||
                                    decodedToken?.roles.includes(roles.SECRETARY) ||
                                    decodedToken?.roles.includes(roles.PROFESSOR) ||
                                    decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR) ||
                                    decodedToken?.roles.includes(roles.MARKETING_MANAGER)) ? (
                                        <>
                                            <Field>
                                                <Select
                                                    name="participantsSelect"
                                                    label={t("calendar.participants")}
                                                    options={participants}
                                                    selectedValue={values.participantsSelect}
                                                    onChange={(event) => {
                                                        const participantId = event.target.value;

                                                        const updated = values.participants.includes(participantId)
                                                            ? values.participants
                                                            : [...values.participants, participantId];

                                                        setSelectedParticipants(updated);
                                                        setFieldValue("participants", updated);
                                                        setFieldValue("participantsSelect", "");
                                                    }}
                                                />
                                            </Field>
                                        </>
                                ) : null}
                                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR) ||
                                    decodedToken?.roles.includes(roles.SECRETARY)) ? (
                                        <>
                                            <Field>
                                                <Select
                                                    name="groupsSelect"
                                                    label={t("groups.title")}
                                                    options={groupsList}
                                                    selectedValue={values.groupsSelect}
                                                    onChange={(event) => {
                                                        const groupId = event.target.value;

                                                        listGroupParticipants(groupId).then((result) => {
                                                            const students = result?.data?.records || [];
                                                            const studentIds = students.map(s => s.userId);

                                                            const updated = Array.from(
                                                                new Set([...values.participants, ...studentIds])
                                                            );

                                                            setSelectedParticipants(updated);
                                                            setFieldValue("participants", updated);
                                                        });

                                                        setFieldValue("groupsSelect", "");
                                                    }}
                                                />
                                            </Field>
                                        </>
                                    ) : null}
                            </Row>
                        </Form>
                    )}
                </Formik>
                
                <br />
                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
                    decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) ||
                    decodedToken?.roles.includes(roles.DIRECTOR) ||
                    decodedToken?.roles.includes(roles.SECRETARY) ||
                    decodedToken?.roles.includes(roles.PROFESSOR) ||
                    decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR) ||
                    decodedToken?.roles.includes(roles.MARKETING_MANAGER)) ? (
                        <>
                            <List>
                                <ListHeader>
                                    <ListTitle 
                                        title={t("calendar.participants")}
                                        subtitle="List of the Participants who are attached to this event" 
                                    />
                                </ListHeader>
                                <ListBody>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeadCell><span>{t("employees.firstName")}</span></TableHeadCell>
                                            <TableHeadCell><span>{t("employees.lastName")}</span></TableHeadCell>
                                            <TableHeadCell></TableHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                    {selectedParticipants?.length > 0 &&
                                        selectedParticipants?.map((selectedParticipant, index) => {
                                            return (
                                                <TableRow>
                                                    <TableDataCell>{getFirstNameById(selectedParticipant)}</TableDataCell>
                                                    <TableDataCell>{getLastNameById(selectedParticipant)}</TableDataCell>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-trash-can" onClick={() => onRemoveSelectedParticipant(selectedParticipant)} />
                                                    </TableDataCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </ListBody>
                            </List>
                        </>
                    ) : null}
                <br />
                <br />
            </Main>
        </DefaultLayout>
    );
};

export default AddEvent;
